/**
 * Premium "Logo Prism Split" intro.
 *
 * Visual design:
 *  - Three logo copies in cyan / magenta / yellow start fused at center,
 *    then split into a triangle formation (chromatic-prism look) and
 *    re-converge to land as one crisp logo + headline.
 *  - Subtle prismatic spectrum line beneath the logo.
 *
 * Implementation note:
 *   Layers are absolutely positioned via `inset:0` + flex centering on a
 *   wrapper, with GSAP transforms applied to an inner `<div>`. This avoids
 *   the "GSAP transform overrides CSS translate(-50%,-50%)" bug that would
 *   otherwise push the logo off-center as soon as gsap.set adds `scale`.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, brandLogoImg, clipOutroFade, combineSceneFragments, escapeHtml,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

export const logoPrismSplit: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const headline = inputs.text ?? "Allyver";
  const sub = inputs.subtext ?? "";
  const headSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.6;
  const subSize = typeSize("md", ctx.format) * 0.55;
  const eyeSize = typeSize("sm", ctx.format) * 0.7;
  // Logo target height: we use a wordmark logo so prism offsets are tied to
  // a small fraction of width so the layers never reach the frame edge.
  const logoH = isVertical ? 150 : 130;
  // Stage is wide enough to host the "ALLYVER" wordmark with extra room for
  // the prism-split separation distance.
  const stageW = isVertical ? 880 : 1200;
  const stageH = logoH * 2.4;
  // Split offsets — kept conservative so the wordmark never overflows.
  const splitX = isVertical ? 90 : 120;
  const splitY = isVertical ? 110 : 90;

  const t = makeTl(clipStartSec);
  const wrapId = `${clipPrefix}-wrap`;
  const cyanId = `${clipPrefix}-c`;
  const magId = `${clipPrefix}-m`;
  const yelId = `${clipPrefix}-y`;
  const cleanId = `${clipPrefix}-clean`;
  const spectrumId = `${clipPrefix}-spec`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const eyeId = `${clipPrefix}-eye`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  // Each layer = absolute fill + flex-center + inner GSAP target.
  // Inner element receives all transforms; CSS never sets `transform` on it.
  const layer = (id: string, color: string, glow: string) => `
    <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;pointer-events:none;mix-blend-mode:screen;">
      <div id="${id}" style="filter:drop-shadow(0 0 30px ${glow});will-change:transform;">
        ${brandLogoImg({ id: `${id}-img`, sizePx: logoH, color })}
      </div>
    </div>`;

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">
  <div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 36 : 28}px;padding:96px;">
    <div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyeSize}px;letter-spacing:8px;text-transform:uppercase;color:${accent};opacity:0;">— Spectrum —</div>

    <div style="position:relative;width:${stageW}px;max-width:90%;height:${stageH}px;">
      ${layer(cyanId, "#22d3c5", "#22d3c5")}
      ${layer(magId, "#ec4899", "#ec4899")}
      ${layer(yelId, "#fde047", "#fde047")}
      <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;">
        <div id="${cleanId}" style="filter:drop-shadow(0 12px 30px ${withAlpha(accent, 0.55)});will-change:transform;">
          ${brandLogoImg({ id: `${cleanId}-img`, sizePx: logoH })}
        </div>
      </div>
    </div>

    <div id="${spectrumId}" style="height:6px;width:0;background:linear-gradient(90deg, #22d3c5 0%, #a855f7 50%, #ec4899 100%);border-radius:9999px;box-shadow:0 0 16px ${withAlpha(themeColors.fg, 0.4)};"></div>

    <div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headSize}px;line-height:0.95;letter-spacing:-3px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 920 : 1500}px;text-shadow:0 0 60px ${withAlpha(accent, 0.5)};opacity:0;">${escapeHtml(headline)}</div>
    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;letter-spacing:0.4px;color:${withAlpha(themeColors.fg, 0.78)};text-align:center;max-width:${isVertical ? 760 : 1100}px;line-height:1.35;opacity:0;">${escapeHtml(sub)}</div>` : ""}
  </div>
</div>`;

  const init = [
    `gsap.set("#${cyanId}", { x: -${splitX * 1.6}, y: 0, scale: 0.7, opacity: 0 });`,
    `gsap.set("#${magId}", { x: ${splitX * 0.8}, y: -${splitY}, scale: 0.7, opacity: 0 });`,
    `gsap.set("#${yelId}", { x: ${splitX * 0.8}, y: ${splitY}, scale: 0.7, opacity: 0 });`,
    `gsap.set("#${cleanId}", { opacity: 0, scale: 0.65 });`,
    `gsap.set("#${spectrumId}", { width: 0 });`,
    `gsap.set("#${eyeId}", { opacity: 0, y: -16 });`,
    `gsap.set("#${headId}", { opacity: 0, y: 28 });`,
    sub ? `gsap.set("#${subId}", { opacity: 0, y: 18 });` : "",
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    // 1. Triangle split appears.
    t.to(`#${cyanId}`, { opacity: 0.92, scale: 1, duration: 0.55, ease: "raw:\"back.out(1.4)\"" }, 0.05),
    t.to(`#${magId}`, { opacity: 0.92, scale: 1, duration: 0.55, ease: "raw:\"back.out(1.4)\"" }, 0.12),
    t.to(`#${yelId}`, { opacity: 0.92, scale: 1, duration: 0.55, ease: "raw:\"back.out(1.4)\"" }, 0.19),
    // 2. Re-converge to center.
    t.to(`#${cyanId}`, { x: 0, y: 0, duration: 0.55, ease: "raw:\"power3.inOut\"" }, 0.85),
    t.to(`#${magId}`, { x: 0, y: 0, duration: 0.55, ease: "raw:\"power3.inOut\"" }, 0.85),
    t.to(`#${yelId}`, { x: 0, y: 0, duration: 0.55, ease: "raw:\"power3.inOut\"" }, 0.85),
    // 3. Fade out RGB layers as clean logo fades in.
    t.to(`#${cyanId}`, { opacity: 0, duration: 0.35, ease: "raw:\"power2.in\"" }, 1.3),
    t.to(`#${magId}`, { opacity: 0, duration: 0.35, ease: "raw:\"power2.in\"" }, 1.3),
    t.to(`#${yelId}`, { opacity: 0, duration: 0.35, ease: "raw:\"power2.in\"" }, 1.3),
    t.to(`#${cleanId}`, { opacity: 1, scale: 1, duration: 0.5, ease: "raw:\"back.out(1.5)\"" }, 1.3),
    // Spectrum draws.
    t.to(`#${spectrumId}`, { width: isVertical ? 320 : 360, duration: 0.55, ease: "raw:\"power2.out\"" }, 1.55),
    // Headline + eyebrow + sub.
    t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.45, ease: "raw:\"power2.out\"" }, 1.5),
    t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, 1.65),
  ];
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 1.85));

  // Slow dwell: gentle breathing on the clean logo.
  const dwell = Math.max(0.6, clipDurationSec - 2.0);
  tweens.push(t.to(`#${cleanId}`, { scale: 1.04, duration: dwell, ease: "raw:\"sine.inOut\"" }, 2.0));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
